import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { BaseHttpService } from '../../common/services/base-http-service';
import { TokenService } from '../../common/services/token.service';
import { ListParams } from '../../common/interfaces/base-crud.interface';

export interface CheckoutPayload {
  amount: number;
  description?: string;
  cardNumber?: string;
  sheba?: string;
}

export interface CheckoutForUserPayload extends CheckoutPayload {
  user: string;
}

export interface CheckoutResponse {
  redirectUrl?: string;
  [key: string]: any;
}

export interface CheckoutItem {
  id?: string;
  _id?: string;
  amount?: number;
  description?: string;
  cardNumber?: string;
  sheba?: string;
  status?: string;
  trackingCode?: string;
  receipt?: string;
  paymentDate?: string | Date;
  createdAt?: string | Date;
  user?: any;
  requester?: any;
  operator?: any;
  agent?: any;
  [key: string]: any;
}

export interface CheckoutListResult {
  items: CheckoutItem[];
  total: number;
  page: number;
  limit: number;
}

@Injectable()
export class CheckoutService extends BaseHttpService {
  baseUrl: string = environment.baseUrl;

  constructor(http: HttpClient, tokenService: TokenService) {
    super(http, tokenService);
  }

  checkout(payload: CheckoutPayload): Observable<CheckoutResponse> {
    return this.http
      .post<CheckoutResponse | HttpResponse<CheckoutResponse>>(
        `${this.baseUrl}/checkout`,
        payload,
        this.options,
      )
      .pipe(map((res) => this.extractBody<CheckoutResponse>(res)));
  }

  checkoutForUser(
    payload: CheckoutForUserPayload,
  ): Observable<CheckoutResponse> {
    return this.http
      .post<CheckoutResponse | HttpResponse<CheckoutResponse>>(
        `${this.baseUrl}/checkout/for-user`,
        payload,
        this.options,
      )
      .pipe(map((res) => this.extractBody<CheckoutResponse>(res)));
  }

  getCheckoutList(
    page = 1,
    limit = 10,
    options: Pick<ListParams<CheckoutItem>, 'filter' | 'sort'> = {},
  ): Observable<CheckoutListResult> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));

    Object.entries(options.sort || { createdAt: 'DESC' }).forEach(([field, direction]) => {
      params = params.set(`orderBy[${field}]`, direction);
    });

    Object.entries(options.filter || {}).forEach(([field, filter]) => {
      const value = typeof filter === 'string' ? filter : filter?.val;
      if (!value) return;
      const operator = typeof filter === 'string' ? 'like' : filter?.op || 'like';
      params = params.set(`where[${field}][${operator}]`, value);
    });

    return this.http
      .get<any | HttpResponse<any>>(
        `${this.baseUrl}/checkout/me`,
        { ...this.options, params },
      )
      .pipe(map((res) => this.extractListResult(res, page, limit)));
  }

  getCheckoutRequests(
    page = 1,
    limit = 10,
    options: Pick<ListParams<CheckoutItem>, 'filter' | 'sort'> = {},
  ): Observable<CheckoutListResult> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit))
      .set('include', 'user');

    Object.entries(options.sort || { createdAt: 'DESC' }).forEach(([field, direction]) => {
      params = params.set(`orderBy[${field}]`, direction);
    });

    Object.entries(options.filter || {}).forEach(([field, filter]) => {
      const value = typeof filter === 'string' ? filter : filter?.val;
      if (!value) return;
      const operator = typeof filter === 'string' ? 'like' : filter?.op || 'like';
      params = params.set(`where[${field}][${operator}]`, value);
    });

    return this.http
      .get<any | HttpResponse<any>>(
        `${this.baseUrl}/checkout`,
        { ...this.options, params },
      )
      .pipe(map((res) => this.extractListResult(res, page, limit)));
  }

  getCheckoutDetail(id: string): Observable<CheckoutItem> {
    return this.http
      .get<any | HttpResponse<any>>(
        `${this.baseUrl}/checkout/${id}`,
        this.options,
      )
      .pipe(
        map((res) => {
          const body = this.extractBody<any>(res);
          return body?.data || body;
        }),
      );
  }

  acceptCheckout(payload: {
    id: string;
    trackingCode: string;
    receipt: string;
    paymentDate?: Date | string;
  }): Observable<any> {
    return this.http
      .post<any | HttpResponse<any>>(
        `${this.baseUrl}/checkout/accept`,
        payload,
        this.options,
      )
      .pipe(map((res) => this.extractBody<any>(res)));
  }

  getBalance(): Observable<any> {
    return this.http
      .get<any | HttpResponse<any>>(`${this.baseUrl}/balance`, this.options)
      .pipe(map((res) => this.extractBody<any>(res)));
  }

  private extractBody<T>(res: T | HttpResponse<T>): T {
    if (res instanceof HttpResponse) {
      return (res.body || {}) as T;
    }

    return (res || {}) as T;
  }

  private extractListResult(
    res: any | HttpResponse<any>,
    page: number,
    limit: number,
  ): CheckoutListResult {
    const body = this.extractBody<any>(res);
    const data = body?.data ?? body;
    const items =
      [
        data?.items,
        data?.checkouts?.items,
        data?.checkouts,
        body?.items,
        data,
      ].find((candidate) => Array.isArray(candidate)) || [];

    return {
      items,
      total:
        data?.total ?? data?.checkouts?.total ?? body?.total ?? items.length,
      page: data?.page ?? body?.page ?? page,
      limit: data?.limit ?? body?.limit ?? limit,
    };
  }
}
